import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Download, Printer, ChevronLeft, BookOpen, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EBOOK_FILES = {
  "ebook-1": "/ebook-stop-living-paycheck-to-paycheck.md",
  "ebook-2": "/ebook-building-business-credit.md",
  "ebook-3": "/ebook-starting-your-llc.md",
  "ebook-4": "/ebook-business-planning-workbook.md",
  "ebook-5": "/ebook-bankruptcy-to-breakthrough.md",
  "ebook-6": "/ebook-entrepreneur-reset-blueprint.md"
};

const EBOOK_COVERS = {
  "ebook-1": "https://customer-assets.emergentagent.com/job_omw-academy/artifacts/ljfzsvtw_IMG_5405.png",
  "ebook-2": "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=400",
  "ebook-3": "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400",
  "ebook-4": "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400",
  "ebook-5": "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=400",
  "ebook-6": "https://static.prod-images.emergentagent.com/jobs/7884e44f-3dc0-40da-b443-283b24b01218/images/157ee318a0925bc5e7b8db1ae53669c40d6cced6f9f05292b66b73cb12f4b4de.png"
};

const EbookViewerPage = () => {
  const { ebookId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  
  const [ebook, setEbook] = useState(null);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [hasPurchased, setHasPurchased] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get ebook details
        const ebookRes = await axios.get(`${API_URL}/api/ebooks/${ebookId}`);
        setEbook(ebookRes.data);

        // Check if user has purchased
        if (user) {
          try {
            const purchaseCheck = await axios.get(`${API_URL}/api/ebooks/${ebookId}/purchased`, {
              headers: getAuthHeader()
            });
            setHasPurchased(purchaseCheck.data.purchased);
          } catch (e) {
            console.log("Not purchased");
          }
        }

        // Fetch content
        const contentRes = await fetch(EBOOK_FILES[ebookId]);
        const text = await contentRes.text();
        setContent(text);

      } catch (error) {
        console.error("Error:", error);
        toast.error("Ebook not found");
        navigate("/ebooks");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [ebookId, user, getAuthHeader, navigate]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }

  // If not purchased, show preview only
  if (!hasPurchased) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <BookOpen className="h-16 w-16 text-[#1E3A5F] mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-[#1E3A5F] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Purchase Required
          </h1>
          <p className="text-gray-600 mb-8">
            You need to purchase this ebook to view the full content.
          </p>
          <div className="flex gap-4 justify-center">
            <Button 
              onClick={() => navigate("/ebooks")}
              className="bg-[#1E3A5F] hover:bg-[#162c47] text-white"
            >
              Go to Ebook Store
            </Button>
            <Button 
              onClick={() => navigate("/login")}
              variant="outline"
              className="border-[#1E3A5F] text-[#1E3A5F]"
            >
              Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="ebook-viewer-page" className="min-h-screen bg-white">
      {/* Navigation Bar - Hidden when printing */}
      <div className="bg-[#1E3A5F] py-4 print:hidden sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 flex items-center justify-between">
          <button 
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-white hover:text-[#9D9045] transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
            Back to Dashboard
          </button>
          <div className="flex gap-3">
            <Button 
              onClick={handlePrint}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10"
            >
              <Printer className="mr-2 h-4 w-4" />
              Print / Save PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Ebook Content - Optimized for Print */}
      <div className="max-w-4xl mx-auto px-8 py-12 print:py-0 print:px-0 print:max-w-none">
        {/* Cover Page */}
        <div className="text-center mb-16 print:mb-8 print:page-break-after-always">
          <img 
            src={EBOOK_COVERS[ebookId]}
            alt={ebook?.title}
            className="max-w-sm mx-auto mb-8 rounded-lg shadow-lg print:shadow-none print:max-w-xs"
          />
          <h1 className="text-4xl font-bold text-[#1E3A5F] mb-4 print:text-3xl" style={{ fontFamily: 'Playfair Display, serif' }}>
            {ebook?.title}
          </h1>
          <p className="text-xl text-gray-600 print:text-lg">By Lavista Smith</p>
          <p className="text-gray-500 mt-2">OMW Business & Financial Resources</p>
        </div>

        {/* Content */}
        <div className="prose prose-lg max-w-none 
          prose-headings:font-bold prose-headings:text-[#1E3A5F] 
          prose-h1:text-3xl prose-h1:border-b-2 prose-h1:border-[#9D9045] prose-h1:pb-4 prose-h1:mb-8 prose-h1:mt-16 print:prose-h1:page-break-before-always
          prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
          prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
          prose-p:text-gray-700 prose-p:leading-relaxed
          prose-li:text-gray-700
          prose-strong:text-[#1E3A5F]
          prose-table:border-collapse prose-table:w-full
          prose-th:bg-gray-100 prose-th:p-3 prose-th:text-left prose-th:border
          prose-td:p-3 prose-td:border
          print:prose-sm
        ">
          <ReactMarkdown
            components={{
              h1: ({ children }) => (
                <h1 className="text-3xl font-bold text-[#1E3A5F] border-b-2 border-[#9D9045] pb-4 mb-8 mt-16 print:break-before-page" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {children}
                </h1>
              ),
              h2: ({ children }) => (
                <h2 className="text-2xl font-bold text-[#1E3A5F] mt-8 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {children}
                </h2>
              ),
              h3: ({ children }) => (
                <h3 className="text-xl font-semibold text-[#1E3A5F] mt-6 mb-3">
                  {children}
                </h3>
              ),
              hr: () => (
                <hr className="my-12 border-t-2 border-gray-200 print:my-8" />
              ),
              table: ({ children }) => (
                <div className="overflow-x-auto my-6">
                  <table className="w-full border-collapse border border-gray-300">
                    {children}
                  </table>
                </div>
              ),
              th: ({ children }) => (
                <th className="bg-gray-100 p-3 text-left border border-gray-300 font-semibold text-[#1E3A5F]">
                  {children}
                </th>
              ),
              td: ({ children }) => (
                <td className="p-3 border border-gray-300">
                  {children}
                </td>
              ),
              ul: ({ children }) => (
                <ul className="list-disc pl-6 space-y-2 my-4">
                  {children}
                </ul>
              ),
              ol: ({ children }) => (
                <ol className="list-decimal pl-6 space-y-2 my-4">
                  {children}
                </ol>
              ),
              li: ({ children }) => (
                <li className="text-gray-700">
                  {children}
                </li>
              ),
              blockquote: ({ children }) => (
                <blockquote className="border-l-4 border-[#9D9045] pl-4 italic text-gray-600 my-4">
                  {children}
                </blockquote>
              ),
            }}
          >
            {content}
          </ReactMarkdown>
        </div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t-2 border-gray-200 text-center text-gray-500 text-sm print:mt-8">
          <p>© {new Date().getFullYear()} OMW Business & Financial Resources. All Rights Reserved.</p>
          <p className="mt-2">omwbusiness@consultant.com | (877) 295-5905</p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:break-before-page {
            break-before: page;
          }
          @page {
            margin: 1in;
            size: letter;
          }
        }
      `}</style>
    </div>
  );
};

export default EbookViewerPage;
